import React, { useCallback, useMemo } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import BillingPeriodCell from '../BillingPeriodCell';
import { getActiveBillingCycles } from '../../../services/api';
import type {
  ActiveBillingCycleItem,
  EpisodicClaimStatus,
} from '../../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { ClaimAgeCell } from '../ClaimAgeCell';
import { claimAgeDays } from '../claimAge';
import { useBillingCycles } from '../../../hooks/useBillingCycles';
import { useBillingTableQuery } from '../../../hooks/useBillingTableQuery';
import { BillingDataTable } from '../BillingDataTable';
import { BillingActionsCell } from '../BillingActionsCell';
import { ClaimWorksheetViewerAction } from '../ClaimWorksheetViewerAction';
import { SendAgainAction } from '../SendAgainAction';
import { activeBillingCycleNoteContext } from '../billingCycleNoteContext';
import { currencySortingFn } from '../billingTableUtils';
import { formatMoneyDisplay } from '../../../utils/currencyUtils';
import { getClaimStatusBadge } from './claimStatusBadge';
import { PatientCell } from '../PatientCell';
import { InactiveEpisodeBadge } from '../InactiveEpisodeBadge';
import { InsuranceCell } from '../InsuranceCell';
import { NoRemittancesNotice } from '../NoRemittances';
import { PayerReasonBadge } from '../claimReason/PayerReasonBadge';
import { RejectedVoidAction } from '../RejectedVoidAction';

interface EOERejectedCancelledTabProps {
  onCountsInvalidated?: () => Promise<void> | void;
}

const EOERejectedCancelledTab: React.FC<EOERejectedCancelledTabProps> = ({
  onCountsInvalidated,
}) => {
  const query = useBillingTableQuery('rejected-cancelled', {
    sortKeyByColumnId: {
      billing_period: 'billing_period_start',
      status: 'billing_cycle_status',
    },
  });
  const { data, isLoading, isRefreshing, total, totalPages, loadData } =
    useBillingCycles<
      ActiveBillingCycleItem,
      { statuses: EpisodicClaimStatus[] }
    >({
      tabId: 'rejected-cancelled',
      fetchFn: getActiveBillingCycles,
      fetchPayload: {
        statuses: ['REJECTED', 'CANCELLED'],
        ...query.toParams(),
      },
      errorMessage: 'Could not fetch data for the "Rejected / Cancelled" tab.',
      getId: item => item.billing_cycle_id,
      getName: item => item.patient_name,
    });

  // Send-again moves the cycle to Pending Approval, so both the row lists and
  // the tab count badges are stale afterwards. Siblings that move a cycle
  // between tabs pair these two the same way.
  const handleSentAgain = useCallback(async () => {
    await loadData();
    await onCountsInvalidated?.();
  }, [loadData, onCountsInvalidated]);

  const columns = useMemo<ColumnDef<ActiveBillingCycleItem>[]>(
    () => [
      {
        id: 'patient_name',
        accessorKey: 'patient_name',
        header: 'Patient',
        enableSorting: true,
        cell: ({ row }) => (
          <div className='flex flex-col items-start gap-1'>
            <PatientCell
              name={row.original.patient_name}
              mrn={row.original.patient_mrn}
              patientId={row.original.patient_id}
            />
            {row.original.episode_status === 'cancelled' && (
              <InactiveEpisodeBadge className='self-start' />
            )}
          </div>
        ),
      },
      {
        id: 'branch',
        accessorKey: 'branch',
        header: 'Clinic',
        enableSorting: true,
      },
      {
        id: 'insurance_name',
        accessorKey: 'insurance_name',
        header: 'Insurance',
        enableSorting: true,
        cell: ({ row }) => (
          <InsuranceCell
            insuranceName={row.original.insurance_name}
            mbi={row.original.mbi}
            isMedicare={row.original.is_medicare}
            receivesRemittances={row.original.receives_remittances}
          />
        ),
      },
      {
        id: 'billing_period',
        header: 'Billing Period',
        accessorFn: row => row.billing_period_start,
        enableSorting: true,
        cell: ({ row }) => (
          <BillingPeriodCell
            patientId={row.original.patient_id}
            episodeId={row.original.episode_id}
            startDate={formatDateToMMDDYYYY(row.original.billing_period_start)}
            endDate={formatDateToMMDDYYYY(row.original.billing_period_end)}
          />
        ),
      },
      {
        id: 'claim_number',
        accessorKey: 'claim_number',
        header: 'Claim #',
        enableSorting: true,
      },
      {
        id: 'status',
        header: 'Status',
        // TanStack only offers the sort toggle to a column with an accessor.
        accessorFn: row => row.billing_cycle_status,
        enableSorting: true,
        cell: ({ row }) => {
          const badge = getClaimStatusBadge(row.original.billing_cycle_status);
          return (
            <div className='min-w-0'>
              {badge ? (
                <PayerReasonBadge
                  kind='claim'
                  id={row.original.billing_cycle_id}
                  label={badge.label}
                  variant={badge.variant}
                  reasonCount={row.original.reason_count}
                  status={row.original.billing_cycle_status}
                  isRefusal={row.original.billing_cycle_status === 'REJECTED'}
                />
              ) : null}
            </div>
          );
        },
      },
      {
        id: 'total_charges',
        accessorKey: 'total_charges',
        header: 'Total Charges',
        enableSorting: false,
        cell: ({ getValue }) => formatMoneyDisplay(getValue<string | null>()),
        sortingFn: currencySortingFn,
      },
      {
        id: 'claim_age',
        header: 'Claim Age',
        accessorFn: row => claimAgeDays(row.billing_period_end) ?? -1,
        enableSorting: true,
        cell: ({ row }) => (
          <ClaimAgeCell
            periodEnd={row.original.billing_period_end}
            createdAt={row.original.created_at}
            billedAt={row.original.billed_at}
          />
        ),
      },
      {
        id: 'actions',
        header: () => <div className='text-right'>Actions</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <BillingActionsCell
            billingCycleId={row.original.billing_cycle_id}
            billType={row.original.bill_type}
            noteProps={{
              ...activeBillingCycleNoteContext(row.original),
              patientName: row.original.patient_name,
            }}
            leading={
              <>
                <SendAgainAction
                  kind='claim'
                  id={row.original.billing_cycle_id}
                  status={row.original.billing_cycle_status}
                  canResend={row.original.can_resend ?? false}
                  hasPayerEvidence={row.original.has_payer_evidence ?? false}
                  hasPayerClaimControlNumber={
                    row.original.has_payer_claim_control_number ?? false
                  }
                  onDone={handleSentAgain}
                />
                <RejectedVoidAction
                  id={row.original.billing_cycle_id}
                  status={row.original.billing_cycle_status}
                  onDone={handleSentAgain}
                />
                <ClaimWorksheetViewerAction billingCycle={row.original} />
              </>
            }
          />
        ),
      },
    ],
    [handleSentAgain]
  );

  return (
    <div className='flex flex-col gap-4'>
      <NoRemittancesNotice rows={data} />
      <BillingDataTable
        data={data}
        columns={columns}
        getRowId={row => row.billing_cycle_id}
        query={query}
        total={total}
        totalPages={totalPages}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
      />
    </div>
  );
};

export default EOERejectedCancelledTab;
